import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import CompetencyMatrixBuilder from "../components/CompetencyMatrixBuilder";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function EditJob() {
  const { jobId } = useParams();
  const [organization, setOrganization] = useState("");
  const [title, setTitle] = useState("");
  const [jdText, setJdText] = useState("");
  const [matrix, setMatrix] = useState([]);
  const [instructions, setInstructions] = useState("");
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/jobs/${jobId}`).then(({ data }) => {
      setOrganization(data.organization || "");
      setTitle(data.title);
      setJdText(data.jd_text);
      setMatrix(data.competency_matrix);
      setInstructions(data.eval_instructions || "");
      setLoaded(true);
    });
  }, [jobId]);

  const submit = async (e) => {
    e.preventDefault();
    const total = matrix.reduce((s, c) => s + Number(c.weight || 0), 0);
    if (total !== 100) {
      toast.error("Competency weights must total exactly 100%");
      return;
    }
    setLoading(true);
    try {
      await api.put(`/jobs/${jobId}`, { organization, title, jd_text: jdText, competency_matrix: matrix, eval_instructions: instructions });
      toast.success("Job updated!");
      navigate(`/app/jobs/${jobId}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (!loaded) return <div className="text-sm text-muted-foreground">Loading job...</div>;

  return (
    <div className="mx-auto max-w-2xl animate-in fade-in-0 slide-in-from-bottom-2 duration-500">
      <Link to={`/app/jobs/${jobId}`} className="mb-6 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="edit-job-back-link">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to job
      </Link>
      <h1 className="font-heading text-2xl font-bold tracking-tight">Edit Job</h1>
      <form onSubmit={submit} className="mt-6 space-y-6">
        <div>
          <label className="mb-1.5 block text-sm font-medium">Organization</label>
          <Input required value={organization} onChange={(e) => setOrganization(e.target.value)} data-testid="edit-job-organization-input" />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium">Job Role / Title</label>
          <Input required value={title} onChange={(e) => setTitle(e.target.value)} data-testid="edit-job-title-input" />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium">Job Description</label>
          <Textarea required value={jdText} onChange={(e) => setJdText(e.target.value)} className="min-h-[160px]" data-testid="edit-job-jd-textarea" />
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <label className="mb-3 block text-sm font-medium">Competency Matrix</label>
          <CompetencyMatrixBuilder matrix={matrix} setMatrix={setMatrix} />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium">Evaluation Instructions (optional)</label>
          <Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} data-testid="edit-job-instructions-textarea" />
        </div>
        <Button type="submit" disabled={loading} size="lg" data-testid="save-job-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
        </Button>
      </form>
    </div>
  );
}
